import { BASE_API_DEV_URL, BASE_API_PROD_URL } from '@/constants/api'
import axios, { AxiosInstance, AxiosPromise, Cancel } from 'axios'
import {
  ApiRequestConfig,
  WithAbortFn,
  ApiExecutor,
  ApiExecutorArgs,
  ApiError,
} from './api.types'
import i18n from 'i18next'

// Default config for the axios instance
const axiosParams = {
  // Set different base URL based on the environment
  baseURL:
    process.env.NODE_ENV === 'development'
      ? BASE_API_DEV_URL
      : BASE_API_PROD_URL,
}

export const baseAPIURL =
  process.env.NODE_ENV === 'development' ? BASE_API_DEV_URL : BASE_API_PROD_URL
// Create axios instance with default params
export const axiosInstance = axios.create(axiosParams)

export const didAbort = (
  error: unknown
): error is Cancel & { aborted: boolean } => axios.isCancel(error)

const getCancelSource = () => axios.CancelToken.source()

export const isApiError = (error: unknown): error is ApiError => {
  return axios.isAxiosError(error)
}

const withLogger = async <T>(promise: AxiosPromise<T>) =>
  promise.catch((error: ApiError) => {
    if (process.env.NODE_ENV !== 'development') throw error

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      // console.log(error.response.data, 'DATA RECEIVED')
      // console.log(error.response.status, 'STATUS RECEIVED')
      // console.log(error.response.headers, 'HEADERS RECEIVED')
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest
      // in the browser and an instance of
      // http.ClientRequest in node.js console.log(error.request)
    } else {
      // Something happened in setting up the request that triggered an Error
    }

    throw error
  })

const withAbort = <T>(fn: WithAbortFn) => {
  const executor: ApiExecutor<T> = async (...args: ApiExecutorArgs) => {
    const originalConfig = args[args.length - 1] as ApiRequestConfig // Extract abort property from the config
    const { abort, ...config } = originalConfig
    // Create cancel token and abort method only if abort // function was passed
    if (typeof abort === 'function') {
      const { cancel, token } = getCancelSource()
      config.cancelToken = token
      abort(cancel)
    }
    try {
      if (args.length > 2) {
        const [url, body] = args
        return await fn<T>(url, body, config)
      } else {
        const [url] = args
        return await fn<T>(url, config)
      }
    } catch (error) {
      // Add "aborted" property to the error if the request was cancelled
      if (didAbort(error)) {
        error.aborted = true
      }
      throw error
    }
  }
  return executor
}

// Main api function
const api = (axios: AxiosInstance) => {
  return {
    get: <T>(url: string, config: ApiRequestConfig = {}) => {
      const language = i18n.language
      const { headers, ...rest } = config
      const allHeaders = {
        'accept-language': language,
        // 'Cache-Control': 'no-store',
        ...headers,
      }
      return withLogger<T>(
        withAbort<T>(axios.get)(url, {
          withCredentials: true,
          headers: allHeaders,
          ...rest,
        })
      )
    },
    delete: <T>(url: string, config: ApiRequestConfig = {}) => {
      const language = i18n.language
      const { headers, ...rest } = config
      const allHeaders = {
        'accept-language': language,
        // 'Cache-Control': 'no-store',
        ...headers,
      }
      return withLogger<T>(
        withAbort<T>(axios.delete)(url, {
          withCredentials: true,
          headers: allHeaders,
          ...rest,
        })
      )
    },
    post: <T>(url: string, body: unknown, config: ApiRequestConfig = {}) => {
      const language = i18n.language
      const { headers, ...rest } = config
      const allHeaders = {
        'accept-language': language,
        // 'Cache-Control': 'no-store',
        ...headers,
      }
      return withLogger<T>(
        withAbort<T>(axios.post)(url, body, {
          withCredentials: true,
          headers: allHeaders,
          ...rest,
        })
      )
    },
    patch: <T>(url: string, body: unknown, config: ApiRequestConfig = {}) => {
      const language = i18n.language
      const { headers, ...rest } = config
      const allHeaders = {
        'accept-language': language,
        // 'Cache-Control': 'no-store',
        ...headers,
      }
      return withLogger<T>(
        withAbort<T>(axios.patch)(url, body, {
          withCredentials: true,
          headers: allHeaders,
          ...rest,
        })
      )
    },
    put: <T>(url: string, body: unknown, config: ApiRequestConfig = {}) => {
      const language = i18n.language
      const { headers, ...rest } = config
      const allHeaders = {
        'accept-language': language,
        // 'Cache-Control': 'no-store',
        ...headers,
      }
      return withLogger<T>(
        withAbort<T>(axios.put)(url, body, {
          withCredentials: true,
          headers: allHeaders,
          ...rest,
        })
      )
    },
  }
}
export default api(axiosInstance)
